import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Compass, Loader2, AlertTriangle, CheckCircle2, Hammer, Download, ShieldCheck } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const FIELDS = [
  { key: 'injectors', label: 'Injectors', opts: [['oem', 'OEM 13 lb/hr (stock N51)'], ['550cc', '550cc / Ford 47 lb/hr'], ['other', 'Other / unknown']] },
  { key: 'rail_psi', label: 'Rail pressure (PSI)', opts: [['58', '58 (stock)'], ['68', '68'], ['78', '78'], ['86', '86']] },
  { key: 'fuel', label: 'Fuel in tank', opts: [['E85', 'E85'], ['MIXED', 'Mixed E85 / pump gas'], ['E15', 'E15 (accidental)'], ['91', '91 pump gas'], ['93', '93 pump gas']] },
  { key: 'maf', label: 'MAF sensor', opts: [['installed', 'Installed & plugged in'], ['removed', 'Removed / unplugged']] },
  { key: 'knock_sensors', label: 'Knock sensors', opts: [['unknown', 'Unknown'], ['clean', 'Re-torqued, reading clean'], ['noisy', 'Noisy / false knock']] },
  { key: 'goal', label: 'Goal', opts: [['safe', 'Safe daily driver'], ['power', 'Max power'], ['idle', 'Fix idle AFR']] },
  { key: 'flash', label: 'Flash capability', opts: [['full', 'Full flash (BTT / MHD full)'], ['short', 'Short flash only (cal block)']] },
];

function getToken() {
  try { return localStorage.getItem('auth_token'); } catch { return null; }
}

function TuneCard({ tune, primary, onBuild, building, built }) {
  return (
    <div className={`p-4 rounded-sm border ${primary ? 'bg-gradient-to-br from-[#FF5E1A]/10 to-amber-500/5 border-[#FF5E1A]/50' : 'bg-[#050505] border-[#27272A]'}`}
      data-testid={primary ? 'recommend-primary' : `recommend-alt-${tune.endpoint}`}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2">
          {primary ? <CheckCircle2 className="w-4 h-4 text-[#FF5E1A]" /> : <ShieldCheck className="w-4 h-4 text-zinc-500" />}
          <span className={`font-bold font-['Outfit'] ${primary ? 'text-[#FF5E1A]' : 'text-zinc-200'}`}>{tune.label}</span>
        </div>
        <Badge className={`rounded-sm text-[10px] font-['JetBrains_Mono'] ${tune.flash_mode === 'short' ? 'bg-emerald-500/20 text-emerald-300' : 'bg-cyan-500/20 text-cyan-300'}`}>
          {tune.flash_mode.toUpperCase()} FLASH
        </Badge>
      </div>
      <p className="text-xs text-zinc-300 mb-2">{tune.summary}</p>
      <ul className="text-[11px] text-zinc-400 space-y-0.5 mb-3">
        {tune.why.map((w) => <li key={w}>• {w}</li>)}
      </ul>
      {built ? (
        <a href={`${API_URL}${built.clean_download_url || built.download_url}`} target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-xs text-cyan-300 underline" data-testid={`recommend-download-${tune.endpoint}`}>
          <Download className="w-3.5 h-3.5" />Download {built.clean_filename || built.filename}
        </a>
      ) : (
        <Button size="sm" onClick={() => onBuild(tune)} disabled={building}
          className={`rounded-sm text-xs ${primary ? 'bg-[#FF5E1A] hover:bg-[#FF7A3D] text-black font-bold' : 'bg-[#1a1a1a] hover:bg-[#27272A] text-zinc-200'}`}
          data-testid={`recommend-build-${tune.endpoint}`}>
          {building ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Hammer className="w-3.5 h-3.5 mr-1.5" />}
          Build {tune.label}
        </Button>
      )}
    </div>
  );
}

const FUEL_MAP = { E85: 'E85', E15: 'E15', '91': '91', '93': '93', '87': '91', MIXED_E85_91: 'MIXED', MIXED_E85_93: 'MIXED' };

function prefillFromVehicle(v) {
  const hw = v?.hardware || {};
  return {
    ecu: v?.ecu_type || 'MSV80',
    injectors: hw.injectors || 'oem',
    rail_psi: String(hw.rail_psi || 58),
    fuel: FUEL_MAP[v?.current_fuel] || 'E85',
    maf: hw.maf || 'installed',
    knock_sensors: hw.knock_sensors || 'unknown',
    exhaust_cutouts: Boolean(hw.exhaust_cutouts),
    goal: hw.goal || 'safe',
    flash: hw.flash || 'full',
  };
}

export default function TuneRecommender() {
  const { user, refreshUser } = useAuth();
  const vehicle = user?.vehicles?.[0];
  const prefilled = Boolean(vehicle && (vehicle.hardware || vehicle.current_fuel));
  const [form, setForm] = useState(() => prefillFromVehicle(vehicle));
  const [touched, setTouched] = useState(false);
  useEffect(() => {
    if (vehicle && !touched) setForm(prefillFromVehicle(vehicle));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicle?.vehicle_id, vehicle?.current_fuel, vehicle?.hardware]);
  const update = (patch) => { setTouched(true); setForm((f) => ({ ...f, ...patch })); };
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [building, setBuilding] = useState(null);
  const [built, setBuilt] = useState({});

  const headers = () => ({ 'Content-Type': 'application/json', ...(getToken() ? { Authorization: `Bearer ${getToken()}` } : {}) });

  const saveToVehicle = async () => {
    if (!vehicle?.vehicle_id) return;
    const { ecu: _ecu, fuel: _fuel, rail_psi, ...hw } = form;
    await fetch(`${API_URL}/api/auth/vehicles/${vehicle.vehicle_id}/state`, {
      method: 'PATCH', headers: headers(),
      body: JSON.stringify({ hardware: { ...hw, rail_psi: Number(rail_psi) } }),
    }).then(() => refreshUser?.()).catch(() => {});
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError(null); setResult(null); setBuilt({});
    try {
      const res = await fetch(`${API_URL}/api/tunes/recommend`, {
        method: 'POST', headers: headers(),
        body: JSON.stringify({ ...form, rail_psi: Number(form.rail_psi) }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
      setResult(data);
      saveToVehicle();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const build = async (tune) => {
    setBuilding(tune.endpoint); setError(null);
    try {
      const res = await fetch(`${API_URL}/api/tunes/generate/${tune.endpoint}`, { method: 'POST', headers: headers(), body: '{}' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
      setBuilt((b) => ({ ...b, [tune.endpoint]: data }));
    } catch (err) {
      setError(`Build failed: ${err.message}`);
    } finally {
      setBuilding(null);
    }
  };

  return (
    <Card className="bg-[#111111] border border-[#27272A] rounded-sm" data-testid="tune-recommender">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-white font-['Outfit']">
          <Compass className="w-5 h-5 text-[#FF5E1A]" />Find My Starting Tune
        </CardTitle>
        <CardDescription className="text-zinc-400">
          Tell us what is on the car right now and we pick the verified starting bin — no guessing across 15 revisions.
        </CardDescription>
        {prefilled && (
          <p className="text-[11px] text-emerald-300 font-['JetBrains_Mono'] mt-1" data-testid="recommend-prefilled">
            Pre-filled from your vehicle card ({vehicle.year} {vehicle.make} {vehicle.model}) — confirm or adjust, then run.
          </p>
        )}
      </CardHeader>
      <CardContent>
        <form onSubmit={submit} className="grid md:grid-cols-4 gap-3 mb-4" data-testid="recommend-form">
          {FIELDS.map((f) => (
            <label key={f.key} className="text-[11px] uppercase tracking-wider text-zinc-500 font-['JetBrains_Mono']">
              {f.label}
              <select value={form[f.key]} onChange={(e) => update({ [f.key]: e.target.value })}
                className="mt-1 w-full bg-[#050505] border border-[#27272A] rounded-sm px-2 py-2 text-sm text-zinc-200 normal-case tracking-normal font-sans focus:border-[#FF5E1A] outline-none"
                data-testid={`recommend-${f.key}`}>
                {f.opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </label>
          ))}
          <label className="flex items-end gap-2 text-sm text-zinc-300 pb-2">
            <input type="checkbox" checked={form.exhaust_cutouts} onChange={(e) => update({ exhaust_cutouts: e.target.checked })}
              className="accent-[#FF5E1A] w-4 h-4" data-testid="recommend-exhaust_cutouts" />
            Exhaust cut-outs
          </label>
          <div className="md:col-span-4">
            <Button type="submit" disabled={loading} className="bg-[#FF5E1A] hover:bg-[#FF7A3D] text-black font-bold rounded-sm" data-testid="recommend-submit">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Compass className="w-4 h-4 mr-2" />}Recommend My Tune
            </Button>
          </div>
        </form>

        {error && <div className="text-xs text-red-400 mb-3" data-testid="recommend-error">{error}</div>}

        {result && (
          <div className="space-y-3" data-testid="recommend-result">
            <div className="flex flex-wrap items-center gap-2 text-[11px] font-['JetBrains_Mono'] text-zinc-400">
              <span data-testid="recommend-hardware">{result.hardware_summary}</span>
              <Badge className={`rounded-sm text-[10px] ${result.confidence === 'high' ? 'bg-emerald-500/20 text-emerald-300' : result.confidence === 'medium' ? 'bg-amber-500/20 text-amber-300' : 'bg-red-500/20 text-red-300'}`} data-testid="recommend-confidence">
                {result.confidence.toUpperCase()} CONFIDENCE
              </Badge>
            </div>
            {result.warnings.map((w) => (
              <div key={w} className="flex gap-2 p-2.5 bg-amber-500/5 border border-amber-500/30 rounded-sm text-xs text-amber-200" data-testid="recommend-warning">
                <AlertTriangle className="w-4 h-4 shrink-0 text-amber-400" />{w}
              </div>
            ))}
            {result.primary ? (
              <TuneCard tune={result.primary} primary onBuild={build} building={building === result.primary.endpoint} built={built[result.primary.endpoint]} />
            ) : (
              <div className="p-3 bg-[#050505] border border-[#27272A] rounded-sm text-sm text-zinc-300" data-testid="recommend-none">
                No library tune fits this combination yet — follow the warnings above, then re-run.
              </div>
            )}
            {result.alternatives.length > 0 && (
              <div>
                <p className="text-[11px] uppercase tracking-wider text-zinc-500 font-['JetBrains_Mono'] mb-2">Alternatives</p>
                <div className="grid md:grid-cols-2 gap-3">
                  {result.alternatives.map((t) => (
                    <TuneCard key={t.endpoint} tune={t} onBuild={build} building={building === t.endpoint} built={built[t.endpoint]} />
                  ))}
                </div>
              </div>
            )}
            <div className="p-3 bg-[#050505] border border-[#27272A] rounded-sm" data-testid="recommend-next-steps">
              <p className="text-[11px] uppercase tracking-wider text-zinc-500 font-['JetBrains_Mono'] mb-1.5">Next steps</p>
              <ol className="text-xs text-zinc-300 space-y-1 list-decimal list-inside">
                {result.next_steps.map((s) => <li key={s}>{s}</li>)}
              </ol>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
